import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  Dialog,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  ArrowForward,
  ArrowBack,
  Close,
  AccessTime,
  CalendarToday,
  Repeat,
  Schedule,
  LockOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import {
  formatHours,
  formatOperationalDays,
  formatFullAddress,
  formatPrice,
  planPeriodLabel,
  hoursLabel,
  discountedFirstPrice,
  titleCasePlanType,
  getAmenityIcon,
  PlanFeature,
  Reveal,
  AnimatedNumber,
  MotionList,
  MotionItem,
} from './themeHelpers';

const placeholderImages = [
  'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&q=80',
  'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80',
  'https://images.unsplash.com/photo-1568667256549-094345857637?w=800&q=80',
];

// Frosted glass card
function GlassCard({ children, sx = {} }) {
  return (
    <Box
      sx={{
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.22)',
        borderRadius: 4,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function AuroraBackground({ accentColor }) {
  const shouldReduce = useReducedMotion();

  // Extract a complementary second hue (shift the accent slightly)
  const auroraKeyframes = shouldReduce
    ? {}
    : {
        animate: {
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        },
        transition: {
          duration: 18,
          repeat: Infinity,
          ease: 'linear',
        },
      };

  return (
    <Box
      component={shouldReduce ? 'div' : motion.div}
      {...auroraKeyframes}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        backgroundSize: '300% 300%',
        background: `
          radial-gradient(ellipse 80% 60% at 20% 30%, ${accentColor}55 0%, transparent 60%),
          radial-gradient(ellipse 60% 80% at 80% 70%, rgba(147,51,234,0.35) 0%, transparent 55%),
          radial-gradient(ellipse 70% 50% at 50% 10%, rgba(56,189,248,0.25) 0%, transparent 50%),
          linear-gradient(135deg, #0d0d1f 0%, #1a1230 50%, #0d1a2e 100%)
        `,
        pointerEvents: 'none',
      }}
    />
  );
}

function AuroraTheme({ library, plans, accentColor, onBookNow }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const images = library.gallery?.length > 0 ? library.gallery : placeholderImages;
  const galleryImages = images.slice(0, 6);

  const defaultDescription = `Welcome to ${library.name}! Experience a peaceful, light-filled sanctuary designed for deep focus and inspired study.`;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const hoursStr = formatHours(library.open_time, library.close_time);
  const daysStr = formatOperationalDays(library.operational_days);
  const fullAddress = formatFullAddress(library);

  const availSeats = library.available_seats ?? library.capacity;
  const seatDisplay =
    availSeats !== null && availSeats !== undefined && library.capacity
      ? `${availSeats} / ${library.capacity}`
      : library.capacity
      ? String(library.capacity)
      : null;

  const openLightbox = (idx) => { setLightboxIdx(idx); setLightboxOpen(true); };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', color: 'white', overflow: 'hidden' }}>
      <AuroraBackground accentColor={accentColor} />

      {/* Everything sits above the fixed bg */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>

        {/* ── Hero ── */}
        <Box sx={{ position: 'relative', height: { xs: '55vh', md: '75vh' }, overflow: 'hidden' }}>
          <Box
            component="img"
            src={images[currentImageIndex]}
            alt={library.name}
            loading="eager"
            fetchpriority="high"
            decoding="async"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          {/* Overlay */}
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(13,13,31,0.4) 0%, rgba(13,13,31,0.75) 100%)',
          }} />

          {/* Arrows */}
          {images.length > 1 && (
            <>
              <IconButton
                onClick={prevImage}
                aria-label="Previous image"
                sx={{
                  position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                  color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                onClick={nextImage}
                aria-label="Next image"
                sx={{
                  position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)',
                  color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                <ArrowForward />
              </IconButton>
            </>
          )}

          {/* Dots */}
          {images.length > 1 && (
            <Box sx={{ position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
              {images.map((_, idx) => (
                <Box
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  sx={{
                    width: idx === currentImageIndex ? 24 : 8, height: 8,
                    borderRadius: 4, bgcolor: idx === currentImageIndex ? accentColor : 'rgba(255,255,255,0.45)',
                    cursor: 'pointer', transition: 'all 0.3s',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Hero text */}
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: { xs: 3, md: 6 } }}>
            <Container maxWidth="lg">
              <Reveal y={32} duration={0.6}>
                <Typography
                  variant="h2"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: '2rem', md: '3.5rem' },
                    lineHeight: 1.15,
                    letterSpacing: '-0.5px',
                    mb: 1,
                    textShadow: '0 2px 20px rgba(0,0,0,0.5)',
                  }}
                >
                  {library.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, opacity: 0.85 }}>
                  <LocationOn fontSize="small" />
                  <Typography variant="subtitle1">{library.address}</Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onBookNow()}
                  sx={{
                    bgcolor: accentColor,
                    backdropFilter: 'blur(8px)',
                    px: 4, py: 1.5,
                    borderRadius: 3,
                    fontSize: '1rem',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    boxShadow: `0 0 32px ${accentColor}88`,
                    '&:hover': { bgcolor: accentColor, filter: 'brightness(1.12)', boxShadow: `0 0 48px ${accentColor}aa` },
                  }}
                >
                  Book a Seat
                </Button>
              </Reveal>
            </Container>
          </Box>
        </Box>

        {/* ── About + Quick Facts ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Reveal>
                <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3, fontWeight: 600 }}>
                  About
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 2, color: 'white' }}>
                  Your focus sanctuary
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.85, fontSize: '1.05rem' }}>
                  {library.description || defaultDescription}
                </Typography>
              </Reveal>
            </Grid>

            <Grid item xs={12} md={5}>
              <Reveal delay={0.1}>
                <GlassCard sx={{ p: 3 }}>
                  {/* Seats */}
                  {seatDisplay && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 2 }}>
                        Available Seats
                      </Typography>
                      <Typography variant="h3" fontWeight={700} sx={{ color: accentColor, lineHeight: 1.1 }}>
                        <AnimatedNumber value={seatDisplay} />
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>seats</Typography>
                    </Box>
                  )}

                  {/* Lockers */}
                  {library.locker_count > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <LockOutlined sx={{ color: accentColor, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        {library.locker_count} lockers available
                      </Typography>
                    </Box>
                  )}

                  {/* Hours */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: daysStr ? 2 : 0 }}>
                    <AccessTime sx={{ color: accentColor, fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>
                        {hoursStr || 'Contact for hours'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Days */}
                  {daysStr && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mt: 1 }}>
                      <CalendarToday sx={{ color: accentColor, fontSize: 20, mt: 0.2 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>{daysStr}</Typography>
                    </Box>
                  )}
                </GlassCard>
              </Reveal>
            </Grid>
          </Grid>
        </Container>

        {/* ── Plans ── */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3, fontWeight: 600 }}>
                Membership
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 5, color: 'white' }}>
                Choose your plan
              </Typography>
            </Reveal>

            {plans.length > 0 ? (
              <MotionList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 24 }}>
                {plans.map((plan) => {
                  const planName = plan.name || titleCasePlanType(plan.plan_type);
                  const price = formatPrice(plan.price_cents);
                  const period = planPeriodLabel(plan.plan_type);
                  const discPct = plan.first_time_discount;
                  const discPrice = discountedFirstPrice(plan.price_cents, discPct);

                  return (
                    <MotionItem key={plan.id}>
                      <GlassCard
                        sx={{
                          p: 3, height: '100%', position: 'relative',
                          transition: 'transform 0.25s, box-shadow 0.25s',
                          '&:hover': {
                            transform: 'translateY(-6px) scale(1.01)',
                            boxShadow: `0 16px 48px ${accentColor}44`,
                          },
                        }}
                      >
                        {discPct > 0 && (
                          <Chip
                            label={`${discPct}% OFF`}
                            size="small"
                            sx={{
                              position: 'absolute', top: 12, right: 12,
                              bgcolor: accentColor, color: 'white', fontWeight: 700, fontSize: '0.65rem',
                            }}
                          />
                        )}

                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 1, textTransform: 'uppercase', mb: 1, fontSize: '0.7rem' }}>
                          {planName}
                        </Typography>

                        {price && (
                          <>
                            {discPct > 0 && discPrice !== null ? (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through' }}>
                                  {price}
                                </Typography>
                                <Typography variant="h4" fontWeight={700} sx={{ color: accentColor }}>
                                  {formatPrice(discPrice)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                  first time, then {price}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="h3" fontWeight={700} sx={{ color: accentColor, mb: 0.5 }}>
                                {price}
                              </Typography>
                            )}
                            {period && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 2 }}>
                                {period}
                              </Typography>
                            )}
                          </>
                        )}

                        <Box sx={{ mb: 2 }}>
                          {plan.duration_days && (
                            <PlanFeature icon={<CalendarToday />} color="rgba(255,255,255,0.6)">
                              Valid {plan.duration_days} days
                            </PlanFeature>
                          )}
                          <PlanFeature icon={<Schedule />} color="rgba(255,255,255,0.6)">
                            {hoursLabel(plan.hours_per_day)}
                          </PlanFeature>
                          {plan.recurring && (
                            <PlanFeature icon={<Repeat />} color={accentColor}>
                              Auto-renews
                            </PlanFeature>
                          )}
                        </Box>

                        {plan.description ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.5)', display: '-webkit-box',
                              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 2,
                            }}
                          >
                            {plan.description}
                          </Typography>
                        ) : null}

                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={() => onBookNow(plan)}
                          sx={{
                            borderColor: `${accentColor}88`, color: accentColor,
                            backdropFilter: 'blur(4px)',
                            '&:hover': { bgcolor: `${accentColor}22`, borderColor: accentColor },
                            borderRadius: 2,
                          }}
                        >
                          Select Plan
                        </Button>
                      </GlassCard>
                    </MotionItem>
                  );
                })}
              </MotionList>
            ) : (
              <GlassCard sx={{ p: 5, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>Contact us for membership details</Typography>
                <Button variant="contained" onClick={() => onBookNow()} sx={{ bgcolor: accentColor, '&:hover': { filter: 'brightness(1.1)' } }}>
                  Get in Touch
                </Button>
              </GlassCard>
            )}
          </Container>
        </Box>

        {/* ── Gallery ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Reveal>
            <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3, fontWeight: 600 }}>
              Gallery
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 4, color: 'white' }}>
              Step inside
            </Typography>
          </Reveal>
          <Grid container spacing={2}>
            {galleryImages.map((img, idx) => (
              <Grid item xs={6} md={4} key={idx}>
                <Box
                  component="img"
                  src={img}
                  alt={`Gallery ${idx + 1}`}
                  onClick={() => openLightbox(idx)}
                  loading="lazy"
                  decoding="async"
                  sx={{
                    width: '100%', height: 200, objectFit: 'cover',
                    borderRadius: 3, cursor: 'pointer',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': { transform: 'scale(1.03)', boxShadow: `0 8px 32px ${accentColor}55` },
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>

        {/* ── Amenities ── */}
        {library.amenities?.length > 0 && (
          <Box sx={{ py: { xs: 6, md: 10 } }}>
            <Container maxWidth="lg">
              <Reveal>
                <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3, fontWeight: 600 }}>
                  Facilities
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 5, color: 'white' }}>
                  Everything you need
                </Typography>
              </Reveal>
              <MotionList style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 16 }}>
                {library.amenities.map((amenity, idx) => (
                  <MotionItem key={idx}>
                    <GlassCard sx={{ p: 2, textAlign: 'center', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                      <Box sx={{ color: accentColor, mb: 1 }}>
                        {React.cloneElement(getAmenityIcon(amenity), { sx: { fontSize: 32 } })}
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                        {amenity}
                      </Typography>
                    </GlassCard>
                  </MotionItem>
                ))}
              </MotionList>
            </Container>
          </Box>
        )}

        {/* ── Location ── */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
          <Reveal>
            <Typography variant="overline" sx={{ color: accentColor, letterSpacing: 3, fontWeight: 600 }}>
              Location
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, mb: 4, color: 'white' }}>
              Find us here
            </Typography>
          </Reveal>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <GlassCard sx={{ height: 300, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {library.lat && library.lng ? (
                  <iframe
                    title="Library Location"
                    width="100%" height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${library.lat},${library.lng}&zoom=15`}
                  />
                ) : (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Map unavailable</Typography>
                )}
              </GlassCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                {fullAddress && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <LocationOn sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>Address</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>{fullAddress}</Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_phone && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Phone sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>Phone</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        <a href={`tel:${library.contact_phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_phone}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {library.contact_email && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Email sx={{ color: accentColor, mt: 0.3, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>Email</Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        <a href={`mailto:${library.contact_email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {library.contact_email}
                        </a>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* ── CTA ── */}
        <Box sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
          <Container maxWidth="md">
            <Reveal>
              <GlassCard sx={{ p: { xs: 4, md: 6 } }}>
                <Typography variant="h4" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
                  Ready to focus?
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 4 }}>
                  Book your seat at {library.name} and unlock your study potential.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => onBookNow()}
                  sx={{
                    bgcolor: accentColor, px: 5, py: 1.5, borderRadius: 3,
                    boxShadow: `0 0 40px ${accentColor}66`,
                    '&:hover': { filter: 'brightness(1.12)', boxShadow: `0 0 56px ${accentColor}88` },
                    fontWeight: 600, fontSize: '1rem',
                  }}
                >
                  Book Now
                </Button>
              </GlassCard>
            </Reveal>
          </Container>
        </Box>

        {/* ── Footer ── */}
        <Box sx={{ py: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>{library.name}</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                Powered by{' '}
                <a href="https://bookmylibrary.in" target="_blank" rel="noopener noreferrer" style={{ color: accentColor }}>
                  BookMyLibrary
                </a>
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* ── Lightbox ── */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(10,10,20,0.97)', backdropFilter: 'blur(24px)' } }}
      >
        <IconButton
          onClick={() => setLightboxOpen(false)}
          aria-label="Close gallery"
          sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <Box
          component="img"
          src={galleryImages[lightboxIdx]}
          alt="Gallery"
          loading="lazy"
          decoding="async"
          sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
        />
      </Dialog>
    </Box>
  );
}

export default AuroraTheme;
